import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Shield, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n/LangContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Signed in");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen relative grid-bg flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="aurora opacity-60" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand to-brand-cyan glow-blue mb-4">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t("admin.login")}</h1>
          <p className="text-sm text-zinc-400">{t("admin.login_sub")}</p>
        </div>

        <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
              <Mail className="w-3 h-3 inline me-1" /> {t("admin.email")}
            </label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
              <Lock className="w-3 h-3 inline me-1" /> {t("admin.password")}
            </label>
            <input
              required
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-lg glow-blue transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {t("admin.sign_in")}
          </button>
        </form>
      </div>
    </main>
  );
}
